import { Button, Divider, Header, Icon, Loader, Modal, FileDrop, Card } from '@scuf/common';
import Alert from 'components/alert';
import useUser from 'helpers/hooks/useUser';
import { ChatTable } from 'models/repaircenter/detailsRma';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'styles/repaircenter.scss';
import moment from 'moment';
import LabelDescription from './labelDescription';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { RmaFilterModel, RmaModelLite } from 'models';
import { useRecoilState } from 'recoil';
import { FilterAtom, REPAIR_CENTER, RmaLite, COMPANY_MANAGER } from 'resources';
import useFetch from 'helpers/hooks/useFetch';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { EmailServiceType } from 'models/repaircenter/detailsRma';
import { getOrderById } from 'resources/api/spareParts_api/getOrderById';
import { getCompanyById } from 'resources/api/usermanager_api/addCustomer';

type ChatProps = {
    messages: ChatTable[];
    loading: boolean;
    onSend: (message: string, enable: boolean) => Promise<void>;
    companies?: CompanyDTO[];
    chatId?: number;
    onUploadFile: (file: File) => Promise<any>;
    onDownloadFile: (fileNameOrFileId: string, downloadName?: string) => void;
};

const ENV = process.env.NODE_ENV;
const isProd = ENV === 'production';
const isDev = ENV === 'development';
const isQa = ENV === 'test';

const subjectPrefix = isQa || isDev ? 'TESTE DE ATRASO EM AMBIENTE DE QA OU DEV: ' : '';
const ignoreString = isQa || isDev ? 'IGNORE ESSE email É APENAS UM TESTE! ' : '';

const SUPERADMIN = '1';
const ADMIN = '2';

const Chat: React.FC<ChatProps> = ({ onSend, loading, messages, companies, chatId, onUploadFile, onDownloadFile }) => {
    const { t } = useTranslation(['repaircenter', 'common']);
    const navigate: NavigateFunction = useNavigate();
    const [enableFinish, setEnableFinish] = useState(true);
    const [messageToSend, setMessageToSend] = useState('');
    const [sending, setSending] = useState(false);
    const chatRef = useRef<HTMLDivElement>(null);
    const { user } = useUser();
    const [rmaState, setRmaState] = useRecoilState<RmaModelLite>(RmaLite);
    const [rmaFilter, setRmaFilter] = useRecoilState<RmaFilterModel>(FilterAtom);
    const [loadingChat, setLoadingChat] = useState(false);
    const [, sendEmail] = useFetch<EmailServiceType>({
        url: COMPANY_MANAGER.sendDelayEmail,
        method: 'POST',
        onError: t('common:translation.EmailError'),
        onSuccess: t('common:translation.EmailSent')
    });
    const [dataRma, getRmaFilter, loadingRmas] = useFetch<RmaFilterModel, RmaFilterModel>({
        url: REPAIR_CENTER.getRmas,
        method: 'POST',
        redirectOnError: true
    });

    const [uploadModal, setUploadModal] = useState(false);
    const [chatFile, setChatFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [disableUploadBtn, setDisableUploadBtn] = useState(true);

    const isAdmin = user?.role === SUPERADMIN || user?.role === ADMIN;

    const delayMessage = 'Prezado Cliente/Parceiro! Gostaríamos de informá-lo que identificamos um atraso na liberação do seu pedido junto à nossa fábrica. ' +
        'Estamos cientes da importância desse pedido e, por isso, nossa equipe está dedicada a resolver essa situação com a máxima urgência. ' +
        'Agradecemos a sua compreensão e paciência. Se precisar de mais informações ou tiver alguma dúvida, por favor, não hesite em nos contatar. '

    const finishMessage = 'Obrigado(a) pelo contato! Caso tenha alguma dúvida ou necessite de assistência, estamos à disposição. ' +
        'Informamos que não é necessário responder a esta mensagem. ' +
        'Tenha um ótimo dia!'

    useEffect(() => {
        const rmaId = messages?.[0]?.rmaId;
        if (rmaId && !rmaState?.id) {
            getRmaFilter({ ...rmaFilter, indice: 1, id: rmaId });
        }
    }, [messages]);

    useEffect(() => {
        if (dataRma) {
            const id = dataRma.data?.rmas?.[0]?.id;
            if (id) {
                setRmaState({ ...rmaState, id });
                navigate(`rma-detail/${id}`);
            }
        }
    }, [dataRma]);

    useEffect(() => {
        if (messageToSend.length > 500) {
            setMessageToSend(messageToSend.slice(0, 500));
        }
    }, [messageToSend]);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
    }, [messages.length]);

    useEffect(() => {
        setDisableUploadBtn(chatFile == null);
    }, [chatFile]);

    const handleUpload = async () => {
        if (!chatFile || !user?.ssoid) return;
        setUploading(true);
        try {
            const ext = chatFile.name.split('.').pop();
            const name = chatFile.name.split('.').slice(0, -1).join('.');
            const timestamp = Date.now();
            const newFileName = `${name}_${timestamp}.${ext}`;
            const newFile = new File([chatFile], newFileName, { type: chatFile.type });
            const response = await onUploadFile(newFile);
            if (!response?.error) {
                const savedName = response.data.fileName;
                const fileId = response.data.id;
                const realName = chatFile.name;
                const fileMessage = fileId ? `${realName}||${savedName}||${fileId}` : `${realName}||${savedName}`;
                await onSend(fileMessage, true);
            }
        } finally {
            setUploading(false);
            setUploadModal(false);
            setChatFile(null);
        }
    };

    const sendMessage = async (enabled: boolean) => {
        setSending(true);
        setEnableFinish(enabled);
        try {
            const text = enabled ? messageToSend : finishMessage;
            await onSend(text, enabled);
            setMessageToSend('');
        } finally {
            setSending(false);
        }
    };

    const handleClick = async () => {
        if (messages?.[0]?.orderId) {
            navigate('order', { state: { Id: messages?.[0]?.orderId } });
        } else if (messages?.[0]?.rmaId) {
            setLoadingChat(true);
            await getRmaFilter({ ...rmaFilter, indice: 1, id: messages[0].rmaId });
            setLoadingChat(false);
        }
    };

    return (
        <>
            {uploadModal && (
                <Modal open={uploadModal} onClose={() => setUploadModal(false)} size="small">
                    <FileDrop
                        placeholder={t('common:actions.Draganddropfileshere')}
                        resetButtonText={t('common:actions.Delete')}
                        onAccepted={files => setChatFile(files[0])}
                        onReset={() => setChatFile(null)}
                    />
                    <Card.Footer>
                        <Button content={t('common:actions.Close')} onClick={() => setUploadModal(false)} />
                        <Button
                            type="primary"
                            icon="slidercontrols-plus"
                            onClick={handleUpload}
                            content={t('common:actions.Create')}
                            disabled={disableUploadBtn}
                            loading={uploading}
                            iconPosition="right"
                        />
                    </Card.Footer>
                </Modal>
            )}

            {loadingChat && loading && loadingRmas ? (
                <Loader text={'Loading'} overlayOpacity={0.5} />
            ) : (
                <div className="container-chat1">
                    <div className="chat-display" ref={chatRef}>
                        <Header
                            icon={<Icon name="sites" root="common" size="large" />}
                            style={{ textAlign: 'center' }}
                            title={companies?.find(x => x.id === messages?.[0]?.companyId)?.name ?? ''}
                            className="header"
                            logo={false}
                            menu={false}
                        />

                        {!window.location.href.includes('rma-detail') && !window.location.href.includes('order') && (
                            <div
                                className="no-padding blue-text"
                                style={{ margin: '12px', cursor: 'pointer', textAlign: 'center', textDecoration: 'underline' }}
                                onClick={handleClick}
                            >
                                {messages?.[0]?.orderId ? (
                                    <LabelDescription title={t('common:translation.Redirect') + ' #' + messages[0].orderId} />
                                ) : messages?.[0]?.rmaId ? (
                                    <LabelDescription title={t('common:translation.Redirect') + ' #' + messages[0].rmaId} />
                                ) : null}
                            </div>
                        )}

                        {messages?.length > 0 ? (
                            messages.map(item => {
                                const [realName, savedName, fileId] = item.message.split('||');
                                return (
                                    <div
                                        key={item.id}
                                        className={user?.ssoid === item?.ssoId ? 'sended-message' : 'received-message'}
                                    >
                                        <h5>
                                            <Icon name="user" size="medium" />
                                            {item?.userName}
                                            <Divider fitted />
                                        </h5>
                                        <p className="text-message">
                                            {savedName ? (
                                                <span
                                                    onClick={() =>
                                                        fileId
                                                            ? onDownloadFile(fileId.toString(), realName)
                                                            : onDownloadFile(savedName, realName)
                                                    }
                                                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                                >
                                                    {realName}
                                                </span>
                                            ) : (
                                                item.message
                                            )}
                                        </p>
                                        <Divider fitted />
                                        <p className="date-message">
                                            <Icon name="calendar" size="medium" />
                                            {moment(item.createdAt).format('YYYY-MM-DD[,] HH:mm:ss')}
                                        </p>
                                    </div>
                                );
                            })
                        ) : (
                            <Alert text={t('common:empty.NoChatAlert')} />
                        )}
                    </div>

                    <div className="container-input">
                        <textarea
                            className="message-input"
                            placeholder={t('common:placeholders.Input')}
                            maxLength={500}
                            value={messageToSend}
                            onChange={e => setMessageToSend(e.target.value)}
                            rows={3}
                        />

                        {isAdmin && messages?.[0]?.rmaId == null && (
                            <Button
                                icon={<Icon name="badge-warning" />}
                                iconPosition="right"
                                size="medium"
                                loading={sending}
                                content={t('common:translation.DelayedPart')}
                                onClick={async () => {
                                    setSending(true);
                                    const localOrder = await getOrderById(chatId ?? 0);
                                    const company = await getCompanyById(localOrder.data.companyId);
                                    const emailList = `${localOrder.data.email.replace(/;/g, ',')},${process.env.REACT_APP_SPARE_PARTS_EMAIL ?? ''
                                        }`;

                                    await sendEmail({
                                        NewCompany: false,
                                        Body: '',
                                        Subject: `${subjectPrefix} AVISO DE ATRASO ${ignoreString}`,
                                        ToEmail: isProd ? emailList ?? "" : user.email ?? process.env.REACT_APP_SPARE_PARTS_EMAIL,
                                        Fields: {
                                            email: '',
                                            pv: localOrder.data.numberPV ?? '',
                                            companyName: ignoreString + company.data.name,
                                            cnpj: '',
                                            order: localOrder.data.id.toString()
                                        }
                                    });

                                    await onSend(
                                        delayMessage,
                                        true
                                    );
                                    setSending(false);
                                }}
                            />
                        )}

                        {isAdmin && (
                            <Button
                                icon={<Icon name="close" />}
                                iconPosition="right"
                                size="medium"
                                loading={sending}
                                disabled={!enableFinish}
                                content={t('common:actions.Endchat')}
                                onClick={() => sendMessage(false)}
                            />
                        )}

                        <Button
                            icon={<Icon name="camera" />}
                            iconPosition="right"
                            size="medium"
                            content="Upload"
                            onClick={() => setUploadModal(true)}
                        />

                        <Button
                            className="send-button"
                            icon={<Icon name="double-caret-right" />}
                            iconPosition="right"
                            size="medium"
                            loading={sending}
                            disabled={!messageToSend.length || sending}
                            content={t('common:actions.Send')}
                            onClick={() => sendMessage(true)}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default Chat;